import {
  Viewer,
  Cartesian3,
  Cartesian2,
  Cartographic,
  CallbackProperty,
  Color,
  Entity,
  ConstantPositionProperty,
  JulianDate,
} from "cesium";
import { radiansToDegrees } from "@turf/helpers";
import { createMaterialMarchingAnts } from "../utils/createMaterialMarchingAnts";
import { AttachablePluginInterface } from "./CesiumWrapper";
import { ActivatableToolInterface } from "./ActivatableToolInterface";
import { BackgroundableToolInterface } from "./BackgroundableToolInterface";
import {
  SerializableToolInterface,
  compareToolState,
} from "./SerializableToolInterface";
import EventEmitter, { EventEmitterInterface } from "./EventEmitter";
import PositionRayPickerPlugin from "./PositionRayPickerPlugin";
import {
  ObservableProperty,
  DerivedObservableProperty,
  ReadonlyObservableProperty,
} from "./ObservableProperty";

type CompleteEvent = {
  points: { longitude: number; latitude: number; height: number }[];
};
type ToolStage = "picking" | "complete";

export type DrawPolygonToolSnapshot =
  | DrawPolygonToolSnapshotV1
  | DrawPolygonToolSnapshotV2;
type DrawPolygonToolSnapshotV1 = {
  version?: 1;
  toolStage: ToolStage;
  points: { longitude: number; latitude: number; height: number }[];
};
type DrawPolygonToolSnapshotV2 = {
  version: 2;
  points: { longitude: number; latitude: number; height: number }[];
};

function migrateState(
  state: DrawPolygonToolSnapshot | undefined,
): DrawPolygonToolSnapshotV2 {
  if (!state || state.version === 2) return state as DrawPolygonToolSnapshotV2;
  return {
    version: 2,
    points: state.points,
  };
}

export function isDrawPolygonStateApplied(
  state: DrawPolygonToolSnapshot | undefined,
): boolean {
  state = migrateState(state);
  return (state?.points?.length ?? 0) > 0;
}

export default class DrawPolygonToolPlugin
  implements
    AttachablePluginInterface,
    ActivatableToolInterface,
    SerializableToolInterface<DrawPolygonToolSnapshot>,
    BackgroundableToolInterface
{
  private _viewer: Viewer = null;
  private _isActive = false;
  private _completeEvent = new EventEmitter<CompleteEvent>();
  private _points: Cartesian3[] = [];
  private _entitiesPoints: Entity[] = [];
  private _entityPolyline: Entity = null;
  private _detachClickHandlerFunc: () => void;
  private _detachMoveHandlerFunc: () => void;

  private readonly _toolStage: ObservableProperty<ToolStage> =
    new ObservableProperty("picking");
  private readonly _completedPoints = new ObservableProperty<
    CompleteEvent["points"] | null
  >(null);
  readonly isApplied = new DerivedObservableProperty(
    () => isDrawPolygonStateApplied(this.capture()),
    [this._completedPoints],
  );

  get completeEvent(): EventEmitterInterface<CompleteEvent> {
    return this._completeEvent;
  }

  get toolStage(): ReadonlyObservableProperty<ToolStage> {
    return this._toolStage;
  }

  get completedPoints(): ReadonlyObservableProperty<
    CompleteEvent["points"] | null
  > {
    return this._completedPoints;
  }

  constructor(private _positionRayPicker: PositionRayPickerPlugin) {}

  setViewer(viewer: Viewer) {
    this._viewer = viewer;
  }

  detachViewer() {
    this._viewer = null;
  }

  setActive(value: boolean) {
    this._isActive = value;
    if (value) {
      this.attachClickHandler(this._positionRayPicker.leftClickEvent);
      this.attachMoveHandler(this._positionRayPicker.moveEvent);
    } else {
      this._detachClickHandlerFunc?.();
      this._detachMoveHandlerFunc?.();
      this._viewer?.scene.requestRender();
    }
  }

  clear(): void {
    this._toolStage.set("picking");
    this._completedPoints.set(null);
    this._points = [];
    if (this._viewer) {
      this.updateLine();
      this._viewer.scene.requestRender();
    }
  }

  private attachClickHandler(
    clickEvent: typeof this._positionRayPicker.leftClickEvent,
  ) {
    this._detachClickHandlerFunc?.();
    this._detachClickHandlerFunc = clickEvent.addHandler((ev) => {
      if (
        !this._viewer ||
        !this._isActive ||
        this._toolStage.getSnapshot() !== "picking"
      )
        return;

      let isCompleting = false;
      if (this._points.length > 1) {
        const entityFirstPoint = this._entitiesPoints[0];
        if (this.isHoveringPoint(ev.screenPosition, entityFirstPoint)) {
          isCompleting = true;
        }
      }
      if (!isCompleting) {
        if (!ev.position) return;
        this._points[this._points.length] = ev.position;
      } else {
        this.complete();
      }
      this.updateLine();
      this._viewer.scene.requestRender();
    });
  }

  private attachMoveHandler(
    moveEvent: typeof this._positionRayPicker.moveEvent,
  ) {
    this._detachMoveHandlerFunc?.();
    this._detachMoveHandlerFunc = moveEvent.addHandler((ev) => {
      if (
        !this._viewer ||
        !this._isActive ||
        this._toolStage.getSnapshot() !== "picking"
      )
        return;

      let position = ev.position;
      if (this._points.length > 1) {
        const entityFirstPoint = this._entitiesPoints[0];
        if (this.isHoveringPoint(ev.screenPosition, entityFirstPoint))
          position = entityFirstPoint.position.getValue(JulianDate.now());
      }

      if (!position) return;
      const iPoint = Math.max(0, this._points.length - 1);
      this._points[iPoint] = position;
      this.updateLine();
      this._viewer.scene.requestRender();
    });
  }

  private isHoveringPoint(screenPosition: Cartesian2, pointEntity: Entity) {
    if (!pointEntity) return false;
    const picks = this._viewer.scene.drillPick(screenPosition);
    return picks.some(
      (e) =>
        e.id?.properties?.type?.valueOf() === "polygonPoint" &&
        e.id?.id === pointEntity.id,
    );
  }

  private complete() {
    this._toolStage.set("complete");
    const points = this._points.map<CompleteEvent["points"][0]>((position) => {
      const cartographic = Cartographic.fromCartesian(position);
      if (!cartographic) return null;
      return {
        longitude: radiansToDegrees(cartographic.longitude),
        latitude: radiansToDegrees(cartographic.latitude),
        height: cartographic.height,
      };
    });
    this._completedPoints.set(points);
    this._completeEvent.emit({ points });
  }

  private updateLine() {
    const entitiesPointsUpdated = this._points.map((position, i) => {
      const entity = this._entitiesPoints[i];
      if (entity) {
        entity.position = new ConstantPositionProperty(position);
        return entity;
      }
      return this.addEntityPoint(position);
    });
    this._entitiesPoints
      .splice(entitiesPointsUpdated.length)
      .forEach((entity) => this._viewer.entities.remove(entity));
    this._entitiesPoints = entitiesPointsUpdated;

    if (this._points.length <= 1) {
      this.removeEntityPolylineIfPresent();
    } else {
      if (!this._entityPolyline)
        this._entityPolyline = this.addEntityPolyline();
    }
  }

  private addEntityPoint(position: Cartesian3) {
    if (!this._viewer) return;
    return this._viewer.entities.add({
      position: new ConstantPositionProperty(position),
      point: {
        pixelSize: 12,
        color: Color.WHITE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
      properties: {
        type: "polygonPoint",
        excludeFromPicking: true,
      },
    });
  }

  private addEntityPolyline() {
    if (!this._viewer) return;
    const positions = new CallbackProperty(() => {
      const isJoining = this._toolStage.getSnapshot() === "complete";
      return isJoining ? [...this._points, this._points[0]] : this._points;
    }, false);
    const material = createMaterialMarchingAnts(this._viewer);
    return this._viewer.entities.add({
      polyline: {
        positions,
        width: 2,
        material,
        depthFailMaterial: material,
      },
      properties: {
        type: "polygonLine",
        excludeFromPicking: true,
      },
    });
  }

  private removeEntitiesPointsIfPresent() {
    this._entitiesPoints?.forEach((entity) =>
      this._viewer?.entities.remove(entity),
    );
    this._entitiesPoints = [];
  }

  private removeEntityPolylineIfPresent() {
    if (this._entityPolyline)
      this._viewer?.entities.remove(this._entityPolyline);
    this._entityPolyline = null;
  }

  isActive(): boolean {
    return this._isActive;
  }

  activate(): void {
    this.setActive(true);
  }

  deactivate(): void {
    this.setActive(false);
  }

  isStateApplied(state: DrawPolygonToolSnapshot | undefined): boolean {
    return isDrawPolygonStateApplied(state);
  }

  isMatchingState(state: DrawPolygonToolSnapshot | undefined): boolean {
    state = migrateState(state);
    return compareToolState(this.capture(), state, (s) =>
      this.isStateApplied(s),
    );
  }

  capture(): DrawPolygonToolSnapshot {
    return {
      version: 2,
      points: this._completedPoints.getSnapshot() ?? [],
    };
  }

  restore(state: DrawPolygonToolSnapshot | undefined): void {
    if (!state?.points?.length) return;
    state = migrateState(state);
    this._points = state.points.map((point) => {
      const cartographic = Cartographic.fromDegrees(
        point.longitude,
        point.latitude,
        point.height,
      );
      if (!cartographic) return null;
      return Cartesian3.fromRadians(
        cartographic.longitude,
        cartographic.latitude,
        cartographic.height,
      );
    });
    this.updateLine();
    this.complete();
    this._viewer?.scene.requestRender();
  }
}
